import React, { useEffect, useMemo, useState } from "react";
import useAxios from "../utils/useAxios";
import "./Notification.css";

const FIELD_LABELS = {
  full_name: "Name",
  phone_number: "Mobile",
  whatsapp_number: "WhatsApp",
  email: "Email",
  date_of_birth: "Date of birth",
  residential_address: "Address",
  city: "City",
  state: "State",
  zip_code: "Zip code",
  pan_no: "PAN",
  want_80g: "80G",
  is_address_confirmed: "Address confirmation",
  is_booked_calendar: "Calendar booking",
  connectivity_status: "Connectivity status",
  profile_pic_url: "Profile photo",
};

const formatFieldName = (field) =>
  FIELD_LABELS[field] ||
  field
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const parseChanges = (changes) => {
  if (!changes) return {};
  if (typeof changes === "string") {
    try {
      return JSON.parse(changes);
    } catch {
      return {};
    }
  }
  return changes;
};

const parseDate = (dateTime) => new Date(String(dateTime).replace(" ", "T"));

const formatDateTime = (dateTime) => {
  // const parsedDate = parseDate(dateTime);

  // if (Number.isNaN(parsedDate.getTime())) return dateTime || "-";

  // return parsedDate.toLocaleString("en-IN", {
  //   day: "2-digit",
  //   month: "short",
  //   year: "numeric",
  //   hour: "2-digit",
  //   minute: "2-digit",
  //   hour12: true,
  // });

  return new Date(dateTime + "Z")?.toLocaleString();
};

const getDateKey = (dateTime) => {
  const parsedDate = parseDate(dateTime);

  if (Number.isNaN(parsedDate.getTime())) return "Unknown date";

  return parsedDate.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isToday = (dateTime) => {
  const parsedDate = parseDate(dateTime);
  const today = new Date();

  return (
    !Number.isNaN(parsedDate.getTime()) &&
    parsedDate.getFullYear() === today.getFullYear() &&
    parsedDate.getMonth() === today.getMonth() &&
    parsedDate.getDate() === today.getDate()
  );
};

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not set";
  return String(value);
};

const Notification = () => {
  const axiosAPI = useAxios();

  const [logs, setLogs] = useState([]);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchChangeLogs = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const response = await axiosAPI.get(
        "/api/dashboard/get-user-change-logs",
      );

      setLogs(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching user change logs:", error);
      setErrorMessage("Unable to load notifications. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChangeLogs();
  }, []);

  const summary = useMemo(() => {
    const todayLogs = logs.filter((log) => isToday(log.created_at));
    const uniqueUsers = new Set(logs.map((log) => log.user_id).filter(Boolean));
    const fieldCounts = {};

    logs.forEach((log) => {
      Object.keys(parseChanges(log.changes)).forEach((field) => {
        fieldCounts[field] = (fieldCounts[field] || 0) + 1;
      });
    });

    const mostChangedField =
      Object.entries(fieldCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    return {
      totalChangesToday: todayLogs.length,
      totalUsersUpdated: uniqueUsers.size,
      mostChangedField: mostChangedField
        ? formatFieldName(mostChangedField)
        : "No changes yet",
    };
  }, [logs]);

  const groupedLogs = useMemo(() => {
    return logs.reduce((groups, log) => {
      const groupKey = getDateKey(log.created_at);

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }

      groups[groupKey].push(log);
      return groups;
    }, {});
  }, [logs]);

  const renderSummaryText = (log) => {
    const changedFields = Object.keys(parseChanges(log.changes));

    if (changedFields.length === 0) {
      return "User profile was updated";
    }

    return `User updated profile fields: ${changedFields
      .map(formatFieldName)
      .join(", ")}`;
  };

  return (
    <div className="container notification-page">
      <div className="page-inner">
        <div className="notification-header">
          <div>
            <span className="notification-kicker">Audit notifications</span>
            <h3 className="mb-1">User Profile Change History</h3>
            <p className="mb-0">Track profile updates from  audit logs.</p>
          </div>

          <button
            className="btn btn-light btn-round"
            type="button"
            onClick={fetchChangeLogs}
            disabled={loading}
            title="Refresh"
          >
            <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
          </button>
        </div>

        <div className="row notification-summary-row">
          <div className="col-md-4">
            <div className="notification-summary-card">
              <span>Total changes today</span>
              <strong>{summary.totalChangesToday}</strong>
            </div>
          </div>

          <div className="col-md-4">
            <div className="notification-summary-card">
              <span>Total users updated</span>
              <strong>{summary.totalUsersUpdated}</strong>
            </div>
          </div>

          {/* <div className="col-md-4">
            <div className="notification-summary-card">
              <span>Most changed field</span>
              <strong>{summary.mostChangedField}</strong>
            </div>
          </div> */}
        </div>

        <div className="card notification-card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0">Notifications ({logs.length})</h4>
            <span className="notification-helper">
              Click any log to view field-wise changes
            </span>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="notification-state">
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p>Loading change history...</p>
              </div>
            ) : errorMessage ? (
              <div className="alert alert-danger mb-0">{errorMessage}</div>
            ) : logs.length === 0 ? (
              <div className="notification-empty">
                <i className="fas fa-bell-slash"></i>
                <h5>No notifications found</h5>
                <p>No user profile changes are available right now.</p>
              </div>
            ) : (
              <div className="notification-timeline">
                {Object.entries(groupedLogs).map(([dateLabel, dateLogs]) => (
                  <div className="notification-day-group" key={dateLabel}>
                    <div className="notification-date-label">{dateLabel}</div>

                    {dateLogs.map((log) => {
                      const changes = parseChanges(log.changes);
                      const changeEntries = Object.entries(changes);
                      const isExpanded = expandedLogId === log.id;

                      // console.log("Parsed changes for log ID", log.id, changes);

                      return (
                        <div
                          className={`notification-log ${
                            isExpanded ? "is-expanded" : ""
                          }`}
                          key={log.id}
                        >
                          <button
                            className="notification-log-main"
                            type="button"
                            onClick={() =>
                              setExpandedLogId(isExpanded ? null : log.id)
                            }
                          >
                            <span className="notification-dot"></span>

                            <span className="notification-log-content">
                              <span className="notification-time">
                                {formatDateTime(log.created_at)}
                              </span>

                              <span className="notification-title">
                                {renderSummaryText(log)}
                              </span>

                              <span className="notification-meta">
                                User ID: {log.user_id || "-"} •{" "}
                                {changeEntries.length} field
                                {changeEntries.length === 1 ? "" : "s"} changed
                              </span>
                            </span>

                            <i
                              className={`fas fa-chevron-down notification-chevron ${
                                isExpanded ? "rotate" : ""
                              }`}
                            ></i>
                          </button>

                          <div
                            className={`notification-details ${
                              isExpanded ? "show" : ""
                            }`}
                          >
                            {changeEntries.length === 0 ? (
                              <div className="notification-no-change">
                                No field details available.
                              </div>
                            ) : (
                              changeEntries.map(([field, value]) => (
                                <div className="change-row" key={field}>
                                  <div className="change-field">
                                    {formatFieldName(field)}
                                  </div>

                                  <div className="change-values">
                                    <div className="change-value old">
                                      <span>Old</span>
                                      <strong>
                                        {getDisplayValue(value?.old)}
                                      </strong>
                                    </div>

                                    <div className="change-value new">
                                      <span>New</span>
                                      <strong>
                                        {getDisplayValue(value?.new)}
                                      </strong>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notification;
